import { ref } from 'vue';
import { useApi } from './useApi';
import { useAppState } from './useAppState';
import { getHttpInstance, getApiConfig } from './useApi';
// Set true by pages that add/update an investment account so the next
// investment-info read (e.g. MoneyPage) knows to SWR+poll until the cached
// /InvestmentHubAccounts payload reflects the write.
export const pendingInvestmentRevalidate = ref(false);
// Dedupe in-flight pollUntilChanged cycles so concurrent callers attach to the
// existing poll instead of each starting their own loop.
let investmentInfoPolling = false;
const investmentInfoWaiters = [];
// Stable change key for the investment-info SWR poll, used only as a fallback
// when the server hash is unavailable. Built from just the sorted account ids
// so adding/removing a pension flips the key, while volatile fields (balances)
// never bail the poll out early.
export function investmentInfoChangeKey(info) {
    return (info ?? []).map((a) => a.id).sort().join(',');
}
export function useInvestments() {
    const api = useApi();
    const state = useAppState();
    function getInvestmentInfo(onSuccess = null, refreshCache = false, clearCache = false, staleWhileRevalidate = false, pollUntilChanged = false) {
        const config = getApiConfig();
        const uri = config.api + (config.useCachedEndpoints ? '/Cached' : '') + "/Customers/00000000-0000-0000-0000-000000000000/InvestmentHubAccounts";
        if (clearCache) {
            localStorage.removeItem(uri);
            return;
        }
        // A pollUntilChanged cycle is already running — attach this caller's
        // callback to it rather than starting a second poll loop. Fire the stale
        // emission synchronously from state.investmentInfo so the new caller gets
        // the cached payload immediately (matches the SWR contract).
        if (pollUntilChanged && investmentInfoPolling) {
            if (onSuccess) {
                if (state.investmentInfo)
                    onSuccess(/* isStale */ true);
                investmentInfoWaiters.push(onSuccess);
            }
            return;
        }
        if (pollUntilChanged)
            investmentInfoPolling = true;
        api.GET(uri, (result, isStale) => {
            state.investmentInfo = result;
            if (onSuccess)
                onSuccess(isStale);
            if (!isStale && pollUntilChanged) {
                investmentInfoPolling = false;
                const waiters = investmentInfoWaiters.splice(0);
                waiters.forEach(cb => cb(/* isStale */ false));
            }
        }, !refreshCache, refreshCache, /* ttlMins */ undefined, /* retrying */ false, staleWhileRevalidate, pollUntilChanged, investmentInfoChangeKey);
    }
    function getInvestmentProviders(onSuccess = null) {
        api.GET("/InvestmentProviders", (result) => {
            state.investmentProviders = result;
            if (onSuccess)
                onSuccess(result);
        });
    }
    function getInvestmentTypes(onSuccess = null) {
        api.GET("/InvestmentTypes", (result) => {
            state.investmentTypes = result;
            if (onSuccess)
                onSuccess(result);
        }, true);
    }
    function getInvestmentAccount(id, onSuccess = null, refreshCache = false, staleWhileRevalidate = false, pollUntilChanged = false, pollIntervalMs) {
        api.GET("/Customers/00000000-0000-0000-0000-000000000000/InvestmentHubAccounts/" + id, (result, isStale) => {
            state.investmentAccount = result;
            if (onSuccess)
                onSuccess(result, isStale);
        }, !refreshCache, refreshCache, undefined, false, staleWhileRevalidate, pollUntilChanged, undefined, pollIntervalMs);
    }
    function addInvestment(investment, onSuccess = null) {
        getHttpInstance().post(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/InvestmentHubAccounts", investment)
            .then((response) => { if (onSuccess)
            onSuccess(response.data); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => addInvestment(investment, onSuccess));
            }
        });
    }
    function updateInvestment(investment, onSuccess = null) {
        getHttpInstance().put(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/InvestmentHubAccounts/" + investment.id, investment)
            .then((response) => { if (onSuccess)
            onSuccess(response.data); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => updateInvestment(investment, onSuccess));
            }
        });
    }
    function deleteInvestmentAccount(id, onSuccess = null) {
        getHttpInstance().delete(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/InvestmentHubAccounts/" + id)
            .then((response) => { if (onSuccess)
            onSuccess(response.data); });
    }
    return {
        getInvestmentInfo,
        getInvestmentProviders,
        getInvestmentTypes,
        getInvestmentAccount,
        addInvestment,
        updateInvestment,
        deleteInvestmentAccount,
    };
}
